import React, {
	createContext,
	type Dispatch,
	type PropsWithChildren,
	type SetStateAction,
	useContext,
	useLayoutEffect,
	useState,
} from 'react';

import noop from 'lodash/noop';

import type { GlobalSideNavigationProps } from '../../common/types';

type PeekMenuId = GlobalSideNavigationProps['peekMenuId'];

// While part of the same state, we provide `peekMenuId` and `setPeekMenuId` in separate contexts.
// Some components will depend on `peekMenuId`, which will change.
// Others will only depend on `setPeekMenuId`, which will never change.
export const PeekMenuIdContext = createContext<PeekMenuId>(undefined);
export const SetPeekMenuIdContext = createContext<Dispatch<SetStateAction<PeekMenuId>>>(noop);

export const usePeekMenuId = () => useContext(PeekMenuIdContext);
export const useSetPeekMenuId = () => useContext(SetPeekMenuIdContext);

type PeekMenuIdProviderProps = PropsWithChildren<{ value: PeekMenuId }>;

export const PeekMenuIdProviders = ({ children, value }: PeekMenuIdProviderProps) => {
	// There are two ways for `peekMenuId` to change.
	// One is if the `peekMenuId` prop (i.e. `value`) changes, likely due to a route change.
	// The other is internally via `setPeekMenuId`, e.g. the more menu.
	// Each will override the other, the last one to change/call will be the current state.
	const [peekMenuId, setPeekMenuId] = useState(value);

	// If the `peekMenuId` prop (i.e. `value`) changes, set state before render.
	// Note, we don't need to validate `peekMenuId`.
	// If `peekMenuId` doesn't match an existing menu id, it's equivalent to setting undefined.
	useLayoutEffect(() => {
		setPeekMenuId(value);
	}, [value]);

	return (
		<PeekMenuIdContext.Provider value={peekMenuId}>
			<SetPeekMenuIdContext.Provider value={setPeekMenuId}>
				{children}
			</SetPeekMenuIdContext.Provider>
		</PeekMenuIdContext.Provider>
	);
};
